import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Sentry } from "@/lib/monitoring";
import { supabase } from "@/integrations/supabase/client";

// /sentry-test — manual production verification page.
// Renders only basic UI so it works even if the rest of the app is broken.
export default function SentryTest() {
  const [frontendId, setFrontendId] = useState<string | null>(null);
  const [backendId, setBackendId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const isProd = import.meta.env.PROD;

  const tag = () => new Date().toISOString();

  const triggerFrontendCapture = () => {
    const id = Sentry.captureException(new Error(`Sentry frontend smoke test — ${tag()}`));
    setFrontendId(id || "(no id — Sentry disabled in this build)");
  };

  const triggerFrontendThrow = () => {
    // Will be caught by ErrorBoundary + global handler.
    setTimeout(() => {
      throw new Error(`Sentry frontend uncaught smoke test — ${tag()}`);
    }, 0);
  };

  const triggerBackend = async () => {
    setBusy(true);
    setBackendId(null);
    try {
      const { data, error } = await supabase.functions.invoke("sentry-smoke-test", {
        body: { tag: tag() },
      });
      // Edge wrapper returns 500 with { sentry_event_id } body and header.
      // supabase-js exposes the body as `error.context.body` for non-2xx,
      // but also returns parsed JSON via `data` for some setups — handle both.
      const id =
        (data && (data as Record<string, unknown>).sentry_event_id) ||
        (error && (error as { context?: { sentry_event_id?: string } }).context?.sentry_event_id) ||
        null;
      setBackendId(id ? String(id) : "(no id — see Sentry dashboard)");
    } catch (e) {
      setBackendId(`error: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container mx-auto max-w-2xl py-10 space-y-6">
      <h1 className="text-2xl font-bold">Sentry smoke test</h1>
      <p className="text-sm text-muted-foreground">
        Build mode: <code>{import.meta.env.MODE}</code> · production:{" "}
        <code>{String(isProd)}</code> · release:{" "}
        <code className="font-mono break-all">{__APP_RELEASE__}</code>
      </p>
      {!isProd && (
        <Card className="border-amber-300 bg-amber-50 dark:bg-amber-950/20">
          <CardContent className="p-4 text-sm">
            Sentry initialises only in <code>import.meta.env.PROD</code> builds. Frontend events will
            NOT be sent from the preview — open this page on the published production site
            (e.g. <code>tipomatic.app/sentry-test</code>) to verify. The backend test still works
            from anywhere because edge functions always use the <code>SENTRY_DSN</code> secret.
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle>Frontend — captureException</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Button onClick={triggerFrontendCapture}>Send frontend event</Button>
          {frontendId && (
            <p className="text-sm">
              Event ID: <code className="font-mono break-all">{frontendId}</code>
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Frontend — uncaught throw</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Button variant="destructive" onClick={triggerFrontendThrow}>
            Throw uncaught (check Sentry, no ID returned)
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Backend — edge function throw</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Button onClick={triggerBackend} disabled={busy}>
            {busy ? "Calling..." : "Call sentry-smoke-test"}
          </Button>
          {backendId && (
            <p className="text-sm">
              Event ID: <code className="font-mono break-all">{backendId}</code>
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
